/**
 * Single-part landed cost for tariff_v2, laid out like the v1 TariffCalculator
 * (form card → Calculation Results tiles → breakdown table → FYI tariffs).
 * General duty defaults from the HTS schedule; Section 232/301 come from the
 * official program lists. Unit cost is entered by hand, USD only (product
 * owner, 2026-10). A total that leaves something unpriced is shown as
 * incomplete with the reasons, never blocked.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaExclamationTriangle } from 'react-icons/fa';
import { FiAlertTriangle, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { TiCalculator } from 'react-icons/ti';

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('userToken')}` } });
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
const INPUT = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-so-green-500 focus:border-transparent transition-colors duration-200';

export default function CalculatorV2({ primary }) {
    const scheduleRate = primary?.schedule?.general_rate?.ad_valorem_pct;
    const [form, setForm] = useState({
        htsCode: primary?.hts?.code || '', coo: '', unitCost: '', quantity: 1,
        generalDuty: scheduleRate ?? '', entryDate: '',
    });
    const [countries, setCountries] = useState([]);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [fyiOpen, setFyiOpen] = useState(false);

    useEffect(() => {
        axios.get(`${BASE_URL}/ocr/tariff-v2/countries/`, auth())
            .then((res) => setCountries(res.data?.countries || []))
            .catch(() => setCountries([]));
    }, []);

    const calculate = async () => {
        if (!form.coo || form.unitCost === '') {
            toast.error('Select the country of origin and enter the unit cost');
            return;
        }
        setBusy(true);
        try {
            const body = { hts_code: form.htsCode, coo: form.coo, unit_cost: Number(form.unitCost), quantity: Number(form.quantity) || 1 };
            if (form.generalDuty !== '' && form.generalDuty !== null) body.general_duty_pct = Number(form.generalDuty);
            if (form.entryDate) body.entry_date = form.entryDate;
            const res = await axios.post(`${BASE_URL}/ocr/tariff-v2/calculate/`, body, auth());
            setResult(res.data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Calculation failed — check the inputs');
        } finally {
            setBusy(false);
        }
    };

    const main = (result?.breakdown || []).filter((b) => b.item_type !== 'threatened');
    const threatened = (result?.breakdown || []).filter((b) => b.item_type === 'threatened');

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-300 overflow-hidden p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TiCalculator className="w-5 h-5 text-[#1e5045]" /> Landed Cost Calculator
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">HTS Code</label>
                        <input value={form.htsCode} onChange={(e) => setForm({ ...form, htsCode: e.target.value })}
                            className={`${INPUT} font-mono`} placeholder="XXXX.XX.XXXX" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin</label>
                        <select value={form.coo} onChange={(e) => setForm({ ...form, coo: e.target.value })} className={INPUT}>
                            <option value="">Select Country</option>
                            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost (USD)</label>
                        <input type="number" min="0" step="0.01" value={form.unitCost}
                            onChange={(e) => setForm({ ...form, unitCost: e.target.value })} className={INPUT} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                        <input type="number" min="1" value={form.quantity}
                            onChange={(e) => setForm({ ...form, quantity: e.target.value })} className={INPUT} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">General Duty Rate (%)</label>
                        <input type="number" min="0" step="0.1" value={form.generalDuty}
                            onChange={(e) => setForm({ ...form, generalDuty: e.target.value })} className={INPUT}
                            placeholder={primary?.schedule?.general_rate?.raw || ''} />
                        <p className="text-xs text-gray-500 mt-1">
                            From the HTS schedule: {primary?.schedule?.general_rate?.raw || 'not an ad valorem rate'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date</label>
                        <input type="date" value={form.entryDate} onChange={(e) => setForm({ ...form, entryDate: e.target.value })} className={INPUT} />
                    </div>
                </div>
                <button onClick={calculate} disabled={busy || !form.htsCode}
                    className="px-4 py-2 bg-[#1e5045] text-white font-medium rounded-lg hover:bg-[#163d35] focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:ring-offset-2 transition-colors duration-200 w-full mt-6 disabled:opacity-50">
                    {busy ? 'Calculating...' : 'Calculate Landed Cost'}
                </button>
            </div>

            {result && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-300 overflow-hidden p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Calculation Results</h3>
                    {!result.complete && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex gap-3">
                            <FiAlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-medium text-amber-800">Incomplete — the total leaves out:</p>
                                <ul className="text-sm text-amber-700 list-disc pl-4">{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="p-4 bg-[#f6f4ed] rounded-lg"><div className="text-sm text-gray-500">Base Cost</div>
                            <div className="text-2xl font-bold text-gray-900">{money(result.base_cost)}</div></div>
                        <div className="p-4 bg-[#1e5045] rounded-lg text-white"><div className="text-sm opacity-80">Landed Cost{result.complete ? '' : ' (incomplete)'}</div>
                            <div className="text-2xl font-bold">{money(result.landed_cost)}</div></div>
                        <div className="p-4 bg-[#f26522]/15 rounded-lg"><div className="text-sm text-[#f26522]">Total Tariffs</div>
                            <div className="text-2xl font-bold text-[#f26522]">{money(result.total_tariff)}</div></div>
                        <div className="p-4 bg-[#f6f4ed] rounded-lg"><div className="text-sm text-gray-500">Effective Rate</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {result.base_cost ? ((result.total_tariff / result.base_cost) * 100).toFixed(1) : '0.0'}%</div></div>
                    </div>
                    <div className="overflow-hidden border rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50"><tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                            </tr></thead>
                            <tbody className="divide-y divide-gray-200">
                                {main.map((item, i) => (
                                    <tr key={i} className={item.item_type === 'waived' ? 'bg-gray-50 text-gray-400' : item.is_danger ? 'bg-red-50' : ''}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {item.is_danger && <FaExclamationTriangle className="w-4 h-4 text-red-500" />}
                                                <span className={`font-medium ${item.item_type === 'waived' ? 'line-through' : item.is_danger ? 'text-red-700' : 'text-gray-900'}`}>{item.name}</span>
                                            </div>
                                            {item.note && <div className="text-xs text-gray-500 mt-0.5">{item.note}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-sm">{item.rate || '—'}</td>
                                        <td className={`px-4 py-3 text-right font-mono text-sm ${item.is_danger ? 'text-red-700 font-semibold' : ''}`}>
                                            {item.item_type === 'waived' ? '—' : money(item.cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-[#1e5045] text-white"><tr>
                                <td className="px-4 py-3 font-semibold">Total Landed Cost{result.complete ? '' : ' (incomplete)'}</td><td />
                                <td className="px-4 py-3 text-right font-mono font-bold text-lg">{money(result.landed_cost)}</td>
                            </tr></tfoot>
                        </table>
                    </div>
                    {!!threatened.length && (
                        <div className="mt-4 border-2 border-[#f26522]/30 rounded-lg overflow-hidden bg-[#f26522]/10">
                            <button onClick={() => setFyiOpen(!fyiOpen)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#f26522]/20 transition-colors">
                                <span className="flex items-center gap-2 font-semibold text-[#f26522]">
                                    <FiAlertTriangle className="w-5 h-5" /> Potential Future Tariffs (FYI)
                                </span>
                                {fyiOpen ? <FiChevronUp className="text-[#f26522]" /> : <FiChevronDown className="text-[#f26522]" />}
                            </button>
                            {fyiOpen && (
                                <table className="min-w-full text-sm"><tbody>
                                    {threatened.map((t, i) => (
                                        <tr key={i} className="bg-[#f26522]/5"><td className="px-4 py-2">{t.name}<div className="text-xs text-gray-500">{t.note}</div></td>
                                            <td className="px-4 py-2 text-right font-mono">{t.rate}</td><td className="px-4 py-2 text-right font-mono">{money(t.cost)}</td></tr>
                                    ))}
                                </tbody></table>
                            )}
                        </div>
                    )}
                    <p className="text-xs text-gray-500 mt-3">HTS schedule {result.schedule_revision}</p>
                </div>
            )}
        </div>
    );
}
